/* eslint-disable consistent-return */
import { ErrorMessage } from '@hookform/error-message';
import { ColorSystemPositive2, Spacing8 } from 'autogen/design-tokens/tokens';
import type { ActivateGiftCardSuccess, CheckoutGiftCardLine, RemoveGiftCardLineSuccess } from 'autogen/swagger/all';
import {
    GetGiftCardInfoQueryKey,
    GetGroupedBasketQueryKey,
    useActivateGiftCardMutation,
    useRemoveGiftCardLineMutation
} from 'autogen/swagger/all/hooks';
import {
    Activate,
    CVC as CVCTrans,
    CardNumber as CardNumberTrans,
    GiftCardVerified,
    RemoveGiftCard
} from 'autogen/translation-keys/trans-website-checkout-giftcard';
import { Button } from 'components/Button';
import Icon from 'components/Icon';
import Flex from 'components/Layout/components/Flex';
import LoadingContainer from 'components/checkout/components/BasketOverview/components/LoadingContainer';
import { Typography } from 'components/generic/typography';
import WithIcon from 'components/hoc/withIcon/WithIcon';
import TextInput from 'components/shared/inputs/text';
import { format } from 'modules/helpers/strings';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import queryClient from 'setup/react-query/queryClient';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';
import { ErrorMessage as Message } from '../../shared/styles';

const StyledTextInputWithValidation = styled(TextInput)`
    input {
        width: 100%;
    }
`;

const CardNumberInput = styled(StyledTextInputWithValidation)`
    flex: 2;
`;

const CVCInput = styled(StyledTextInputWithValidation)`
    flex: 1;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: ${Spacing8};
    align-items: end;

    ${breakpoint.up(sizes.sm)} {
        grid-template-columns: 2fr 1fr 1fr;
    }
`;

const StyledButton = styled(Button)`
    flex: 1;
    grid-column: 1 / -1;

    ${breakpoint.up(sizes.sm)} {
        grid-column: auto;
    }
`;

export default function GiftCardLine({
    model: { id, cvc: initialCVC, cardNumber: initialCardNumber, isActive, balance },
    index
}: {
    model: CheckoutGiftCardLine;
    index: number;
}) {
    const { t } = useTranslation();
    const {
        register,
        setError,
        getFieldState,
        clearErrors,
        getValues,
        formState: { errors }
    } = useForm();

    const CVCfieldName = `giftCardLines[${index}].CVC`;
    const cardNumberfieldName = `giftCardLines[${index}].CardNumber`;

    const onSuccess = (data: ActivateGiftCardSuccess | RemoveGiftCardLineSuccess) => {
        if (!data?.validationErrors) {
            queryClient.invalidateQueries({ queryKey: [GetGiftCardInfoQueryKey] });
            queryClient.invalidateQueries({ queryKey: [GetGroupedBasketQueryKey] });
            return clearErrors([CVCfieldName, cardNumberfieldName]);
        }

        const CVCfieldErrors = data.validationErrors[CVCfieldName] || [];
        const cardNumberfieldErrors = data.validationErrors[cardNumberfieldName] || [];

        if (CVCfieldErrors.length) {
            setError(CVCfieldName, { message: CVCfieldErrors.join(' - ') });
        }

        if (cardNumberfieldErrors.length) {
            setError(cardNumberfieldName, { message: cardNumberfieldErrors.join(' - ') });
        }
    };
    const { mutateAsync: activateGiftCard, isLoading: isAdding } = useActivateGiftCardMutation({ onSuccess });
    const { mutateAsync: removeGiftCardLine, isLoading: isRemoving } = useRemoveGiftCardLineMutation({ onSuccess });

    const isLoading = isAdding || isRemoving;

    return (
        <LoadingContainer isLoading={isLoading}>
            <Flex.Column>
                <Grid>
                    <CardNumberInput
                        isInvalid={getFieldState(cardNumberfieldName).invalid}
                        label={t(CardNumberTrans)}
                        defaultValue={initialCardNumber}
                        disabled={isActive}
                        {...register(cardNumberfieldName)}
                    />
                    <CVCInput
                        isInvalid={getFieldState(CVCfieldName).invalid}
                        label={t(CVCTrans)}
                        defaultValue={initialCVC}
                        disabled={isActive}
                        {...register(CVCfieldName)}
                    />

                    {isActive ? (
                        <StyledButton
                            variant="secondary"
                            type="button"
                            onClick={() => removeGiftCardLine({ params: { checkoutGiftCardLineId: id } })}
                        >
                            {t(RemoveGiftCard)}
                        </StyledButton>
                    ) : (
                        <StyledButton
                            type="button"
                            onClick={() => {
                                clearErrors([CVCfieldName, cardNumberfieldName]);
                                activateGiftCard({
                                    params: {
                                        Id: id,
                                        CardNumber: getValues(cardNumberfieldName),
                                        CVC: getValues(CVCfieldName)
                                    }
                                });
                            }}
                        >
                            {t(Activate)}
                        </StyledButton>
                    )}
                </Grid>

                <Flex.Column gap={Spacing8}>
                    <ErrorMessage
                        name={cardNumberfieldName}
                        errors={errors}
                        render={({ message }) => <Message>{message}</Message>}
                    />
                    <ErrorMessage
                        name={CVCfieldName}
                        errors={errors}
                        render={({ message }) => <Message>{message}</Message>}
                    />
                </Flex.Column>

                {isActive && (
                    <Typography.Body as="span" size="S">
                        <WithIcon icon={<Icon type="Checkmark" size={16} fill={ColorSystemPositive2} />}>
                            {format(t(GiftCardVerified))(balance)}
                        </WithIcon>
                    </Typography.Body>
                )}
            </Flex.Column>
        </LoadingContainer>
    );
}
